import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CodeQuestCastle from './CodeQuestCastle';
import ArduinoLEDGame from './ArduinoLEDGame';
import RobotDanceBattle from './RobotDanceBattle';
import RobotDanceArena3D from './RobotDanceArena3D';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

interface Game {
  _id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  platform: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  ageRange: string;
  estimatedTime: string;
  gameType: string;
  isActive: boolean;
  isFeatured: boolean;
  popularity: number;
  rating: number;
  playCount: number;
  completionRate: number;
  averagePlayTime: number;
  bannerImage: string;
  screenshots: string[];
  demoVideo: string;
  tags: string[];
  learningObjectives: string[];
  skills: string[];
  hardware: string[];
  software: string[];
  gameMechanics: string[];
  story: string;
  rules: string[];
  scoring: string;
  progression: {
    levels: number;
    milestones: string[];
    unlockables: string[];
  };
  socialFeatures: {
    multiplayer: boolean;
    sharing: boolean;
    collaboration: boolean;
    competition: boolean;
  };
  accessibility: {
    visual: boolean;
    auditory: boolean;
    motor: boolean;
    cognitive: boolean;
  };
  setup: string[];
  gameplay: string[];
  challenges: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
  achievements: {
    name: string;
    description: string;
    requirements: string;
    reward: string;
  }[];
  leaderboards: string[];
  resources: {
    tutorials: string[];
    videos: string[];
    documentation: string[];
    community: string[];
  };
  safety: string[];
  troubleshooting: {
    commonIssues: string[];
    solutions: string[];
  };
  extensions: string[];
  realWorldApplications: string[];
  educatorNotes: string;
  parentGuide: string;
  difficultyProgression: {
    level: number;
    requirements: string[];
    rewards: string[];
  }[];
  adaptiveLearning: {
    enabled: boolean;
    algorithm: string;
    personalization: string[];
  };
  analytics: {
    tracking: string[];
    metrics: string[];
    insights: string[];
  };
}

const GamePlayRouter: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGame = async () => {
      if (!gameId) {
        setError('No game ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/highcode/games/${gameId}`);
        
        if (response.ok) {
          const gameData = await response.json();
          setGame(gameData);
        } else if (response.status === 404) {
          setError('Game not found');
        } else {
          setError('Failed to load game');
        }
      } catch (err) {
        console.error('Error fetching game:', err);
        setError('Failed to load game');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGame();
  }, [gameId]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Game Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The game you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  // Determine which game component to use based on game properties
  const getGameComponent = () => {
    // Check if it's a sample game by ID
    if (gameId === 'code-quest-castle') {
      return CodeQuestCastle;
    }
    if (gameId === 'arduino-led-challenge') {
      return ArduinoLEDGame;
    }

    // For backend games, determine component based on game properties
    if (game.title.toLowerCase().includes('dance') || game.title.toLowerCase().includes('battle')) {
      // Check if it's specifically the 3D arena version
      if (game.title.toLowerCase().includes('3d') || game.title.toLowerCase().includes('arena')) {
        return RobotDanceArena3D;
      }
      return RobotDanceBattle;
    }
    if (game.title.toLowerCase().includes('castle') || game.title.toLowerCase().includes('quest')) {
      return CodeQuestCastle;
    }
    if (game.title.toLowerCase().includes('arduino') || game.title.toLowerCase().includes('led')) {
      return ArduinoLEDGame;
    }
    if (game.platform.toLowerCase().includes('scratch')) {
      return CodeQuestCastle;
    }
    if (game.platform.toLowerCase().includes('arduino')) {
      return ArduinoLEDGame;
    }
    if (game.category.toLowerCase().includes('robotics')) {
      return RobotDanceBattle;
    }

    // Default to Code Quest Castle for unknown games
    return CodeQuestCastle;
  };

  const GameComponent = getGameComponent();

  return <GameComponent />;
};

export default GamePlayRouter;
